import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Session } from '@models/Sessions/Session';
import { AssetsPipe } from '@pipes/assets.pipe';
import { BusService } from '@services/bus.service';
import { ConfigService } from '@services/config.service';
import { EventsService } from '@services/events.service';
import { IdeaRequestService } from '@services/sessions/idea.request.service';
import { IdeaSocketService } from '@services/sockets/idea.socket.service';

@Component({
  selector: 'app-idea-card',
  templateUrl: './idea-card.component.html',
  styleUrls: ['./idea-card.component.scss'],
})
export class SessionIdeaCardComponent implements OnInit, OnDestroy {
  static ONE_LINER_CHAR_LIMIT = 56;

  @Input() session: Session;
  @Input() showTopic = false;
  @Input() nopreview = false;
  @Input() pickable = false;
  @Input() picked = false;
  @Input() idea: Idea;
  @Input() small = false;
  @Input() xs = false;
  @Output() ideaAction = new EventEmitter<Idea>();
  @Output() pickedChange = new EventEmitter<boolean>();

  @ViewChildren('wrapper') wrappers: QueryList<ElementRef>;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private appConfig: ConfigService,
    private bus: BusService,
    private events: EventsService,
    private ideaRequest: IdeaRequestService,
    private ideaSocket: IdeaSocketService,
    private assetsPipe: AssetsPipe,
  ) {
    if (!this.session && this.idea && this.idea.session) {
      this.session = this.idea.session;
    }
  }

  get thumbnailImage() {
    return this.assetsPipe.transform(
      this.idea.thumbnail_id || 'image/default_idea_thumbnail.png',
    );
  }

  get isCollaborator() {
    const collaborators = this.idea.collaborators.map(a => a.participant_id);
    return collaborators.includes(this.session.me._id);
  }

  get iFollow() {
    return (
      this.idea.i_follow ||
      this.idea.followers.find(
        follower => follower.participant_id === this.session.me._id,
      )
    );
  }

  follow(event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.ideaRequest.follow(this.idea, this.session).subscribe(() => {
      if (!this.iFollow) {
        this.idea.i_follow = true;
        this.idea.followers.push({
          __t: 'Follower',
          participant_id: this.session.me._id,
        });

        this.bus.publish(
          this.events.received.action.session.idea.follow.success,
          { idea: this.idea },
        );
      }
    });
  }

  followed(event) {
    if (event.idea._id === this.idea._id) {
      if (!this.iFollow) {
        this.idea.i_follow = true;
        this.idea.followers.push({
          __t: 'Follower',
          participant_id: this.session.me._id,
        });
      }
    }
  }

  unfollow(event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.ideaRequest.unfollow(this.idea, this.session).subscribe(() => {
      this.idea.i_follow = false;
      this.idea.followers = this.idea.followers.filter(
        follower => follower.participant_id !== this.session.me._id,
      );

      this.bus.publish(
        this.events.received.action.session.idea.unfollow.success,
        { idea: this.idea },
      );
    });
  }

  unfollowed(event) {
    if (event.idea._id === this.idea._id) {
      if (this.iFollow) {
        this.idea.i_follow = false;
        this.idea.followers = this.idea.followers.filter(
          follower => follower.participant_id !== this.session.me._id,
        );
      }
    }
  }

  get oneliner(): string {
    if (this.idea && this.idea.one_liner) {
      if (
        this.idea.one_liner.length >
        SessionIdeaCardComponent.ONE_LINER_CHAR_LIMIT
      ) {
        return (
          this.idea.one_liner.substr(
            0,
            SessionIdeaCardComponent.ONE_LINER_CHAR_LIMIT,
          ) + '...'
        );
      } else {
        return this.idea.one_liner;
      }
    }
    return null;
  }

  get title(): string {
    const LIMIT = 40;
    if (this.idea && this.idea.title) {
      if (this.idea.title.length > LIMIT) {
        return this.idea.title.substr(0, LIMIT) + '...';
      } else {
        return this.idea.title;
      }
    }
    return null;
  }

  ideaClick() {
    this.ideaAction.emit(this.idea);
  }

  pick() {
    if (this.pickable) {
      this.picked = !this.picked;
      this.pickedChange.emit(this.picked);
    }
  }

  ngOnInit() {
    this.bus.subscribe(
      this.events.received.action.session.idea.follow.success,
      this.followed,
      this,
    );
    this.bus.subscribe(
      this.events.received.action.session.idea.unfollow.success,
      this.unfollowed,
      this,
    );
  }

  ngOnDestroy() {
    this.bus.unsubscribe(
      this.events.received.action.session.idea.follow.success,
      this.followed,
    );
    this.bus.unsubscribe(
      this.events.received.action.session.idea.unfollow.success,
      this.unfollowed,
    );
  }
}
